import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { RbacService } from '../rbac/rbac.service';

export interface Login2AuthRequest {
  username: string;
  password: string;
}

export interface Login2AuthResponse {
  token?: string;
  id_token?: string;
  access_token?: string;
  auth_token?: string;
  jwt?: string;
  userId?: string | number;
  user_id?: string | number;
  firstname?: string;
  firstName?: string;
  lastname?: string;
  lastName?: string;
  name?: string;
  email?: string;
  username?: string;
  userName?: string;
  superAdmin?: boolean;
  fullAccess?: boolean;
  permissions?: string[];
  roleNames?: string[];
  administeredCustomerIds?: Array<number | string>;
  accessibleCustomerIds?: Array<number | string>;
  user?: {
    firstname?: string;
    firstName?: string;
    lastname?: string;
    lastName?: string;
    name?: string;
    email?: string;
    username?: string;
    userName?: string;
    userId?: string | number;
    user_id?: string | number;
  };
  data?: Login2AuthResponse;
}

@Injectable({
  providedIn: 'root',
})
export class Login2AuthService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly rbacService = inject(RbacService);
  /** Built from userServiceUrl (same gateway as SSO) — no separate env key required. */
  private readonly authenticateUrl = `${environment.userServiceUrl
    .trim()
    .replace(/\/$/, '')}/authenticate`;

  login(email: string, password: string): Observable<void> {
    const payload: Login2AuthRequest = {
      username: email.trim(),
      password,
    };

    return this.http
      .post<Login2AuthResponse>(this.authenticateUrl, payload, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      })
      .pipe(
        map((response) => {
          const token = this.extractToken(response);
          if (!token) {
            throw new Error('Authentication response did not include a token.');
          }
          this.persistSession(token, email, response);
        })
      );
  }

  /**
   * Writes JWT and profile fields using the same localStorage keys as SSO.
   */
  private persistSession(
    token: string,
    loginEmail: string,
    response: Login2AuthResponse
  ): void {
    const pendingRedirect = this.authService.getRedirectUrl();
    localStorage.clear();
    sessionStorage.clear();
    localStorage.setItem('auth_token', token);
    localStorage.setItem('auth_provider', 'login2');

    const nested = response.user ?? response.data?.user;
    const userId =
      response.userId ??
      response.user_id ??
      nested?.userId ??
      nested?.user_id;
    const firstname =
      response.firstname ??
      response.firstName ??
      nested?.firstname ??
      nested?.firstName;
    const lastname =
      response.lastname ??
      response.lastName ??
      nested?.lastname ??
      nested?.lastName;
    const displayName = response.name ?? nested?.name;
    const email =
      response.email ??
      response.username ??
      response.userName ??
      nested?.email ??
      nested?.username ??
      nested?.userName ??
      loginEmail.trim();

    if (userId != null && String(userId).trim() !== '') {
      localStorage.setItem('user_id', String(userId));
    }
    if (firstname) {
      localStorage.setItem('firstname', firstname);
    }
    if (lastname) {
      localStorage.setItem('lastname', lastname);
    }
    if (displayName) {
      localStorage.setItem('display_name', displayName);
    }
    if (email) {
      localStorage.setItem('user_email', email);
    }

    this.authService.hydrateUserProfileFromToken();
    this.rbacService.seedFromAuthPayload(response);

    if (pendingRedirect) {
      this.authService.setRedirectUrl(pendingRedirect);
    }
  }

  private extractToken(response: Login2AuthResponse): string | null {
    const candidates = [
      response.token,
      response.id_token,
      response.access_token,
      response.auth_token,
      response.jwt,
      response.data?.token,
      response.data?.id_token,
      response.data?.access_token,
      response.data?.auth_token,
      response.data?.jwt,
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }

    return null;
  }
}
